import React, { useState, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { Button } from '@/components/ui/button';
import { zohoAPI } from '@/lib/api';
import { toast } from 'sonner';
import { Webhook, Plus, FolderCog, Loader2, ArrowLeft } from 'lucide-react';
import ZohoFormList from '@/components/zoho/ZohoFormList';
import ZohoFormDetail from '@/components/zoho/ZohoFormDetail';
import ZohoFormCreateModal from '@/components/zoho/ZohoFormCreateModal';
import ZohoCategoryManager from '@/components/zoho/ZohoCategoryManager';

export default function ZohoIntegrationPage() {
  const location = useLocation();
  const [categories, setCategories] = useState([]);
  const [forms, setForms] = useState([]);
  const [modules, setModules] = useState({});
  const [loading, setLoading] = useState(true);
  const [selectedFormId, setSelectedFormId] = useState(location.state?.selectedFormId || null);
  const [createOpen, setCreateOpen] = useState(false);
  const [catManagerOpen, setCatManagerOpen] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [catRes, formRes, modRes] = await Promise.all([
        zohoAPI.getCategories(),
        zohoAPI.getForms({}),
        zohoAPI.getTargetModules(),
      ]);
      setCategories(catRes.data.categories || catRes.data || []);
      setForms(formRes.data.forms || formRes.data || []);
      setModules(modRes.data.modules || modRes.data || {});
    } catch (e) {
      toast.error('Failed to load Zoho data');
    }
    setLoading(false);
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const selectedForm = forms.find(f => f.id === selectedFormId) || null;

  const handleFormCreated = (newForm) => {
    setForms(prev => [newForm, ...prev]);
    setSelectedFormId(newForm.id);
    setCreateOpen(false);
  };

  const handleFormDeleted = (formId) => {
    setForms(prev => prev.filter(f => f.id !== formId));
    if (selectedFormId === formId) setSelectedFormId(null);
  };

  const handleFormUpdated = (updatedForm) => {
    setForms(prev => prev.map(f => f.id === updatedForm.id ? updatedForm : f));
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="zoho-integration-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            {selectedFormId && (
              <Button variant="ghost" size="icon" onClick={() => setSelectedFormId(null)} data-testid="back-to-list-btn">
                <ArrowLeft className="h-5 w-5" />
              </Button>
            )}
            <div>
              <h1 className="text-2xl font-bold flex items-center gap-2">
                <Webhook className="h-6 w-6" />
                Zoho Forms Integration
              </h1>
              <p className="text-muted-foreground text-sm mt-0.5">
                {selectedFormId ? selectedForm?.form_name : `${forms.length} form(s) configured`}
              </p>
            </div>
          </div>
          {!selectedFormId && (
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setCatManagerOpen(true)} data-testid="manage-categories-btn">
                <FolderCog className="h-4 w-4 mr-2" />
                Categories
              </Button>
              <Button onClick={() => setCreateOpen(true)} data-testid="add-form-btn">
                <Plus className="h-4 w-4 mr-2" />
                Add Form
              </Button>
            </div>
          )}
        </div>

        {/* Content */}
        {selectedFormId && selectedForm ? (
          <ZohoFormDetail
            form={selectedForm}
            modules={modules}
            onFormUpdated={handleFormUpdated}
            onFormDeleted={handleFormDeleted}
            onBack={() => setSelectedFormId(null)}
          />
        ) : (
          <ZohoFormList
            forms={forms}
            categories={categories}
            onSelectForm={setSelectedFormId}
            onRefresh={fetchData}
          />
        )}

        {/* Modals */}
        <ZohoFormCreateModal
          open={createOpen}
          onOpenChange={setCreateOpen}
          categories={categories}
          onFormCreated={handleFormCreated}
        />
        <ZohoCategoryManager
          open={catManagerOpen}
          onOpenChange={setCatManagerOpen}
          categories={categories}
          onCategoriesChanged={fetchData}
        />
      </div>
    </DashboardLayout>
  );
}
